import React, { useEffect } from 'react';
import styles from './ClientDetailModal.module.css';
import CloseIcon from '@mui/icons-material/Close';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';

interface ClientProduct {
  name: string;
  ref: string;
  linha: string;
  totalRevenue: number;
  totalVolume: number;
  totalMargem: number;
}

interface ClientDetailModalProps {
  clientName: string;
  clientProducts: ClientProduct[];
  productImages: Record<string, string>;
  productNames: Record<string, string>;
  totalVenda: number;
  totalMargem: number;
  onClose: () => void;
}

const formatCurrency = (value: number) => {
  return value.toLocaleString('pt-BR', {
    style: 'currency', currency: 'BRL',
    notation: 'compact', compactDisplay: 'short',
  });
};

const formatNumber = (value: number) => {
  return value.toLocaleString('pt-BR');
};

const formatDate = (dateStr: string) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

const ClientDetailModal: React.FC<ClientDetailModalProps> = ({
  clientName, clientProducts, productImages, productNames,
  totalVenda, totalMargem, onClose,
}) => {
  const totalPares = clientProducts.reduce((s, p) => s + p.totalVolume, 0);
  const ticketMedio = clientProducts.length > 0 ? totalVenda / clientProducts.length : 0;

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Left panel — conteúdo principal */}
        <div className={styles.leftPanel}>
          {/* Header */}
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <h2 className={styles.title}>{clientName}</h2>
            </div>
            <div className={styles.headerRight}>
              <button className={styles.closeBtn} onClick={onClose}>
                <CloseIcon fontSize="small" />
              </button>
            </div>
          </div>

          <div className={styles.body}>
            {/* Stats row */}
            <div className={styles.statsRow}>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>Faturamento</span>
                <span className={styles.statValue}>{formatCurrency(totalVenda)}</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>Margem</span>
                <span className={styles.statValue}>{formatCurrency(totalMargem)}</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>Ticket Médio</span>
                <span className={styles.statValue}>{formatCurrency(ticketMedio)}</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>Pares</span>
                <span className={styles.statValue}>{formatNumber(totalPares)}</span>
              </div>
            </div>

            {/* Products section */}
            <div>
              <div className={styles.sectionHead}>
                <ShoppingBagOutlinedIcon sx={{ fontSize: 18, color: '#64748b' }} />
                <span className={styles.sectionTitle}>Principais Modelos</span>
                <span className={styles.sectionCount}>
                  {clientProducts.length} produto{clientProducts.length !== 1 ? 's' : ''}
                </span>
              </div>

              {clientProducts.length > 0 ? (
                <div className={styles.productList}>
                  {clientProducts.map((p, i) => {
                    const imgSrc = productImages[p.name];
                    const displayName = productNames[p.name] || p.ref;
                    return (
                      <div key={i} className={styles.productRow}>
                        {imgSrc ? (
                          <img src={imgSrc} alt={p.ref} className={styles.productImg} />
                        ) : (
                          <div className={styles.productImgPlaceholder}>
                            <ImageOutlinedIcon sx={{ fontSize: 20 }} />
                          </div>
                        )}
                        <div className={styles.productInfo}>
                          <div className={styles.productName}>{displayName}</div>
                          <div className={styles.productRef}>{p.ref} · {p.linha}</div>
                        </div>
                        <div className={styles.productValues}>
                          <div className={styles.productVal}>
                            <div className={styles.productValLabel}>Vendas</div>
                            <div className={styles.productValNum}>{formatCurrency(p.totalRevenue)}</div>
                          </div>
                          <div className={styles.productVal}>
                            <div className={styles.productValLabel}>Pares</div>
                            <div className={styles.productValNum}>{formatNumber(p.totalVolume)}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className={styles.emptyState}>
                  <ShoppingBagOutlinedIcon sx={{ fontSize: 32, color: '#e2e8f0' }} />
                  <p>Nenhum produto encontrado para este cliente.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientDetailModal;
